import { useCallback } from "react";
import { useDispatch } from "react-redux";

const useTaggedDispatch = ({ tabKey }) => {
    const dispatch = useDispatch(); // The original, real dispatch

    // We memoize the entire taggedDispatch function.
    // This is crucial for performance (so components don't re-render)
    // and to get a stable function reference we can call recursively.
    const taggedDispatch = useCallback((action) => {

        // 1. Handle Thunks (functions)
        if (typeof action === "function") {
            // Dispatch a *new* thunk that wraps the original.
            // The thunk middleware will call this function with
            // (innerDispatch, getState, extra).
            return dispatch((innerDispatch, getState, extra) => {

                // This is the custom dispatch we'll pass to the thunk.
                const taggedInnerDispatch = (a) => {
                    // If the action 'a' dispatched *inside* the thunk
                    // is *also* a thunk (a function)...
                    if (typeof a === "function") {
                        // ...we call our main `taggedDispatch` function recursively.
                        // This re-runs this whole logic for the nested thunk,
                        // ensuring it *also* gets a tagged dispatch.
                        return taggedDispatch(a);
                    }

                    // If 'a' is a plain action, tag it and dispatch it
                    // using the *real* dispatch.
                    return innerDispatch({
                        ...a,
                        meta: { tabKey, ...(a?.meta || {}) },
                    });
                };

                // Call the user's original thunk, but give it our
                // special `taggedInnerDispatch` instead of the real one.
                return action(taggedInnerDispatch, getState, extra);
            });
        }

        // 2. Handle Plain Actions
        // If it was a plain action from the start, just tag and dispatch.
        return dispatch({
            ...action,
            meta: { tabKey, ...(action.meta || {}) },
        });

    }, [dispatch, tabKey]); // Re-create if dispatch or tabKey changes

    return taggedDispatch;
}

export default useTaggedDispatch;